import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { ContactSummary, ContactStatus, SpeciesBucket } from '../types/contact'
import { AU_STATES } from '../constants/auStates'
import { CONTACT_STATUSES } from '../constants/taxonomy'

export interface ContactFilters {
  species_buckets: SpeciesBucket[]
  min_status: ContactStatus
  region: string | null
}

const DEFAULT_FILTERS: ContactFilters = {
  species_buckets: [],
  min_status: 'unconfirmed',
  region: null,
}

interface ContactState {
  contacts: ContactSummary[]
  selectedContactId: string | null
  filters: ContactFilters
  liveConnected: boolean
  flyTo: ((lon: number, lat: number, zoom?: number) => void) | null
  fitBounds: ((bbox: [number, number, number, number], padding?: number) => void) | null

  setContacts: (contacts: ContactSummary[]) => void
  upsertContact: (contact: ContactSummary) => void
  removeContact: (id: string) => void
  selectContact: (id: string | null) => void
  setFilters: (filters: Partial<ContactFilters>) => void
  toggleSpeciesBucket: (bucket: SpeciesBucket) => void
  setLiveConnected: (connected: boolean) => void
  registerFlyTo: (fn: (lon: number, lat: number, zoom?: number) => void) => void
  registerFitBounds: (fn: (bbox: [number, number, number, number], padding?: number) => void) => void
}

export const useContactStore = create<ContactState>()(
  persist(
    (set) => ({
      contacts: [],
      selectedContactId: null,
      filters: DEFAULT_FILTERS,
      liveConnected: false,
      flyTo: null,
      fitBounds: null,

      setContacts: (contacts) => set({ contacts }),
      registerFlyTo: (fn) => set({ flyTo: fn }),
      registerFitBounds: (fn) => set({ fitBounds: fn }),

      upsertContact: (contact) =>
        set((state) => {
          const idx = state.contacts.findIndex((c) => c.id === contact.id)
          if (idx >= 0) {
            const next = [...state.contacts]
            next[idx] = contact
            return { contacts: next }
          }
          return { contacts: [contact, ...state.contacts] }
        }),

      // Fires when an admin rejects the sole report a contact was built from (see
      // bio_fusion/worker.py's moderation stream handler) — the contact no longer exists.
      removeContact: (id) =>
        set((state) => ({
          contacts: state.contacts.filter((c) => c.id !== id),
          selectedContactId: state.selectedContactId === id ? null : state.selectedContactId,
        })),

      selectContact: (id) => set({ selectedContactId: id }),

      setFilters: (partial) => set((state) => ({ filters: { ...state.filters, ...partial } })),

      toggleSpeciesBucket: (bucket) =>
        set((state) => {
          const current = state.filters.species_buckets
          const next = current.includes(bucket)
            ? current.filter((b) => b !== bucket)
            : [...current, bucket]
          return { filters: { ...state.filters, species_buckets: next } }
        }),

      setLiveConnected: (connected) => set({ liveConnected: connected }),
    }),
    { name: 'bio-contact-filters', partialize: (state) => ({ filters: state.filters }) },
  ),
)

const STATUS_RANK: Record<ContactStatus, number> = Object.fromEntries(
  CONTACT_STATUSES.map((s, i) => [s, i]),
) as Record<ContactStatus, number>

export function selectFilteredContacts(state: ContactState): ContactSummary[] {
  const { contacts, filters } = state
  const regionBbox = filters.region
    ? AU_STATES.find((s) => s.code === filters.region)?.bbox
    : undefined

  return contacts
    .filter((c) => {
      if (
        filters.species_buckets.length > 0 &&
        !filters.species_buckets.includes(c.species_bucket)
      ) {
        return false
      }
      if (STATUS_RANK[c.status] < STATUS_RANK[filters.min_status]) return false
      if (regionBbox) {
        const [minLon, minLat, maxLon, maxLat] = regionBbox
        if (c.lon < minLon || c.lon > maxLon || c.lat < minLat || c.lat > maxLat) return false
      }
      return true
    })
    .sort((a, b) => new Date(b.last_updated_at).getTime() - new Date(a.last_updated_at).getTime())
}
